import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import {
  registerApi,
  type Person,
  type PersonPatch,
  type PersonStatus,
  type PersonHistoryEntry,
} from '../features/register/api.ts';
import { housesApi, type House } from '../features/houses/api.ts';
import { CustomFields } from '../components/CustomFields.tsx';
import { IslandPicker } from '../components/IslandPicker.tsx';
import { dv } from '../i18n/dv.ts';

interface AxiosLikeError { response?: { status?: number; data?: { error?: string } } }

const STATUSES: PersonStatus[] = ['ACTIVE', 'MOVED_OUT', 'DECEASED'];

function MoveControl({ person }: { person: Person }): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data } = useQuery({
    queryKey: ['houses-move-search', debounced],
    queryFn: () => housesApi.list({ page: 1, pageSize: 20, search: debounced || undefined }),
    enabled: debounced.length > 0,
  });

  const move = useMutation({
    mutationFn: (houseId: string) => registerApi.movePerson(person.id, houseId),
    onSuccess: () => {
      setError(null); setDone(true); setSearch('');
      void qc.invalidateQueries({ queryKey: ['person', person.id] });
      void qc.invalidateQueries({ queryKey: ['house', person.house.id] });
    },
    onError: () => setError(dv.common.error),
  });

  const results = (data?.houses ?? []).filter((h: House) => h.id !== person.house.id);

  return (
    <div className="space-y-2">
      <h3 className="status-label text-muted-foreground">{dv.register.movePerson}</h3>
      <input
        className="input"
        placeholder={dv.houses.search}
        value={search}
        onChange={(e) => { setSearch(e.target.value); setDone(false); }}
        aria-label={dv.register.moveTarget}
      />
      {results.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {results.map((h) => (
            <li key={h.id}>
              <button
                onClick={() => { setError(null); move.mutate(h.id); }}
                disabled={move.isPending}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-start transition-colors hover:bg-muted/50"
              >
                <span className="min-w-0">
                  <span className="block font-medium leading-snug truncate">{h.name}</span>
                  {(h.ward || h.address) && (
                    <span className="mono-meta text-muted-foreground block truncate">
                      {[h.ward, h.address].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
                <Icon name="arrow_forward" size={14} className="shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {done && <p className="text-sm text-[hsl(var(--success))]" role="status">{dv.register.moved}</p>}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </div>
  );
}

function PersonEditor({ person }: { person: Person }): JSX.Element {
  const qc = useQueryClient();
  const [fullName, setFullName] = useState(() => person.fullName ?? '');
  const [customData, setCustomData] = useState<Record<string, unknown>>(
    () => ({ ...(person.customData ?? {}) }),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const setName = (value: string): void => {
    setFullName(value);
    setSaved(false);
  };

  const setCustom = (next: Record<string, unknown>): void => {
    setCustomData(next);
    setSaved(false);
  };

  const save = useMutation({
    mutationFn: (patch: PersonPatch) => registerApi.updatePerson(person.id, patch),
    onSuccess: () => {
      setError(null); setSaved(true);
      void qc.invalidateQueries({ queryKey: ['person', person.id] });
    },
    onError: () => setError(dv.common.error),
  });

  return (
    <div className="space-y-6">
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          // Full name is required (the seeded system field); block an empty save.
          if (!fullName.trim()) { setError(dv.register.fullNameRequired); return; }
          save.mutate({ fullName: fullName.trim(), customData });
        }}
      >
        {/* Form-driven sections. The seeded "Basic Information" section renders
            the Full name field, bound to Person.fullName via systemValues. */}
        <CustomFields
          entity="RESIDENT"
          value={customData}
          onChange={setCustom}
          systemValues={{ fullName: { value: fullName, onChange: setName } }}
        />

        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        {saved && !error && <p className="text-sm text-[hsl(var(--success))]" role="status">{dv.common.saved}</p>}

        <div className="flex">
          <button type="submit" disabled={save.isPending} className="btn-primary">
            <Icon name="save" size={15} /> {save.isPending ? dv.common.saving : dv.common.save}
          </button>
        </div>
      </form>

      {/* Status */}
      <StatusControl person={person} />

      {/* Move */}
      <section className="card-paper p-4">
        <MoveControl person={person} />
      </section>
    </div>
  );
}

// Compact owner control shown beside the status badge: a "Mark as owner" button
// that becomes a tonal "Owner" badge once set (click it to remove). Marking
// replaces any existing owner (one owner per house, enforced server-side).
function OwnerToggle({ person }: { person: Person }): JSX.Element {
  const qc = useQueryClient();
  const setOwner = useMutation({
    mutationFn: (owner: boolean) => registerApi.setOwner(person.id, owner),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', person.id] });
      void qc.invalidateQueries({ queryKey: ['house', person.house.id] });
      void qc.invalidateQueries({ queryKey: ['person-history', person.id] });
    },
  });
  const base = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-50';
  return person.isHouseOwner ? (
    <button
      type="button"
      onClick={() => setOwner.mutate(false)}
      disabled={setOwner.isPending}
      title={dv.register.removeOwner}
      className={`${base} bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] hover:bg-[hsl(var(--secondary-container)/0.75)]`}
    >
      <Icon name="workspace_premium" size={12} /> {dv.register.ownerBadge}
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOwner.mutate(true)}
      disabled={setOwner.isPending}
      title={dv.register.markAsOwner}
      className={`${base} border border-border text-muted-foreground hover:bg-muted hover:text-foreground`}
    >
      <Icon name="workspace_premium" size={12} /> {setOwner.isPending ? dv.common.saving : dv.register.markAsOwner}
    </button>
  );
}

// Format an ISO date string as e.g. "13 Jun 2026" for display.
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// An ISO string → yyyy-mm-dd for a <input type="date">, defaulting to today.
function dateInputValue(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
  return d.toISOString().slice(0, 10);
}

// Up-to-two-letter initials for an actor avatar.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]!.charAt(0) + (parts[1]?.charAt(0) ?? '')).toUpperCase();
}

// A short relative time ("just now", "3d ago", "2mo ago") from an ISO string.
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60); if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60); if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24); if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

// Deterministic soft tint for an actor avatar, hashed from the name — drawn
// from the M3 container roles so every tint carries its own on-colour.
const AVATAR_TINTS = [
  'bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]',
  'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]',
  'bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))]',
  'bg-[hsl(var(--success-container))] text-[hsl(var(--on-success-container))]',
  'bg-[hsl(var(--warning-container))] text-[hsl(var(--on-warning-container))]',
  'bg-[hsl(var(--error-container))] text-[hsl(var(--on-error-container))]',
];
function avatarTint(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length]!;
}

// Status section: three buttons (Living / Moved away / Deceased). Moved away and
// Deceased reveal extra inputs that are collected then committed via Apply; Living
// commits immediately. Details are cleared/swapped server-side per status.
function StatusControl({ person }: { person: Person }): JSX.Element {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Which status is being detailed (reveal); null = no reveal showing.
  const [editing, setEditing] = useState<'MOVED_OUT' | 'DECEASED' | 'ACTIVE' | null>(null);
  const [movedLocation, setMovedLocation] = useState(() => person.movedLocation ?? '');
  const [movedDate, setMovedDate] = useState(() => dateInputValue(person.movedDate));
  const [deceasedDate, setDeceasedDate] = useState(() => dateInputValue(person.deceasedDate));
  const [returnedDate, setReturnedDate] = useState(() => dateInputValue(person.returnedDate));

  const status = useMutation({
    mutationFn: (args: { s: PersonStatus; details?: { movedLocation?: string; movedDate?: string; deceasedDate?: string; returnedDate?: string } }) =>
      registerApi.setStatus(person.id, args.s, args.details),
    onSuccess: () => {
      setError(null); setEditing(null);
      void qc.invalidateQueries({ queryKey: ['person', person.id] });
      void qc.invalidateQueries({ queryKey: ['house', person.house.id] });
      void qc.invalidateQueries({ queryKey: ['person-history', person.id] });
    },
    onError: () => setError(dv.common.error),
  });

  const onButton = (s: PersonStatus): void => {
    setError(null);
    if (s === 'ACTIVE') {
      // Coming back FROM moved-away: reveal a returned-date input, commit on Apply.
      if (person.status === 'MOVED_OUT') {
        setReturnedDate(dateInputValue(person.returnedDate));
        setEditing('ACTIVE');
        return;
      }
      // Already living / deceased → living: commit immediately, no return date.
      setEditing(null); status.mutate({ s }); return;
    }
    // Moved away / Deceased: reveal inputs (pre-filled), commit on Apply.
    if (s === 'MOVED_OUT') {
      setMovedLocation(person.movedLocation ?? '');
      setMovedDate(dateInputValue(person.movedDate));
    } else {
      setDeceasedDate(dateInputValue(person.deceasedDate));
    }
    setEditing(s);
  };

  return (
    <section className="card-paper space-y-3 p-4">
      <h2 className="font-serif text-lg font-bold leading-tight">{dv.register.status}</h2>
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onButton(s)}
            disabled={status.isPending}
            aria-pressed={person.status === s}
            className={person.status === s ? 'btn-primary' : 'btn-secondary'}
          >
            {dv.register.statusLabel[s]}
          </button>
        ))}
      </div>

      {/* Recorded info for the current status. */}
      {editing === null && person.status === 'MOVED_OUT' && (
        <p className="mono-meta text-muted-foreground">
          {dv.register.movedSummary(person.movedLocation, fmtDate(person.movedDate))}
        </p>
      )}
      {editing === null && person.status === 'DECEASED' && (
        <p className="mono-meta text-muted-foreground">
          {dv.register.deceasedSummary(fmtDate(person.deceasedDate))}
        </p>
      )}
      {editing === null && person.status === 'ACTIVE' && person.returnedDate && (
        <p className="mono-meta text-muted-foreground">
          {dv.register.returnedSummary(fmtDate(person.returnedDate))}
        </p>
      )}

      {/* Moved away — collect location + date, then Apply. */}
      {editing === 'MOVED_OUT' && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div>
            <label className="field-label" htmlFor="moved-location">{dv.register.movedTo}</label>
            <IslandPicker value={movedLocation} onChange={setMovedLocation} />
          </div>
          <div>
            <label className="field-label" htmlFor="moved-date">{dv.register.movedDate}</label>
            <input
              id="moved-date"
              type="date"
              className="input"
              value={movedDate}
              onChange={(e) => setMovedDate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={status.isPending}
              onClick={() => status.mutate({
                s: 'MOVED_OUT',
                details: {
                  movedLocation: movedLocation.trim() || undefined,
                  movedDate: movedDate || undefined,
                },
              })}
            >
              {dv.register.apply}
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setEditing(null); setError(null); }}>
              {dv.common.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Deceased — collect date, then Apply. */}
      {editing === 'DECEASED' && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div>
            <label className="field-label" htmlFor="deceased-date">{dv.register.deceasedDate}</label>
            <input
              id="deceased-date"
              type="date"
              className="input"
              value={deceasedDate}
              onChange={(e) => setDeceasedDate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={status.isPending}
              onClick={() => status.mutate({ s: 'DECEASED', details: { deceasedDate: deceasedDate || undefined } })}
            >
              {dv.register.apply}
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setEditing(null); setError(null); }}>
              {dv.common.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Living (returning from moved-away) — collect return date, then Apply. */}
      {editing === 'ACTIVE' && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div>
            <label className="field-label" htmlFor="returned-date">{dv.register.returnedDate}</label>
            <input
              id="returned-date"
              type="date"
              className="input"
              value={returnedDate}
              onChange={(e) => setReturnedDate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={status.isPending}
              onClick={() => status.mutate({ s: 'ACTIVE', details: { returnedDate: returnedDate || undefined } })}
            >
              {dv.register.apply}
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setEditing(null); setError(null); }}>
              {dv.common.cancel}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </section>
  );
}

type HistoryKind = 'added' | 'moved' | 'returned' | 'deceased' | 'transferred' | 'edited';

// Per-event visual treatment: a Material Symbols ligature + a tinted node, in
// the M3 container roles (primary / warning / success / tertiary / neutral).
const HISTORY_STYLE: Record<HistoryKind, { icon: string; node: string }> = {
  added: { icon: 'person_add', node: 'bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))] ring-[hsl(var(--border))]' },
  moved: { icon: 'arrow_outward', node: 'bg-[hsl(var(--warning-container))] text-[hsl(var(--on-warning-container))] ring-[hsl(var(--border))]' },
  returned: { icon: 'south_west', node: 'bg-[hsl(var(--success-container))] text-[hsl(var(--on-success-container))] ring-[hsl(var(--border))]' },
  deceased: { icon: 'remove', node: 'bg-[hsl(var(--surface-container-highest))] text-[hsl(var(--muted-foreground))] ring-[hsl(var(--border))]' },
  transferred: { icon: 'home', node: 'bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] ring-[hsl(var(--border))]' },
  edited: { icon: 'edit', node: 'bg-[hsl(var(--surface-container-high))] text-[hsl(var(--muted-foreground))] ring-[hsl(var(--border))]' },
};

// Map a lifecycle change-log entry to its kind, label, optional detail, and the
// date to display (prefer the event's own recorded date, else when it was logged).
function describeHistory(entry: PersonHistoryEntry): { kind: HistoryKind; label: string; detail: string | null; date: string } {
  const meta = entry.metadata ?? {};
  const str = (k: string): string | null => (typeof meta[k] === 'string' ? (meta[k] as string) : null);
  const L = dv.register.historyLabel;
  switch (entry.action) {
    case 'person.added':
      return { kind: 'added', label: L.added, detail: null, date: entry.createdAt };
    case 'person.moved_out':
      return { kind: 'moved', label: L.movedAway, detail: str('movedLocation'), date: str('movedDate') ?? entry.createdAt };
    case 'person.reactivated':
      return { kind: 'returned', label: L.returned, detail: null, date: str('returnedDate') ?? entry.createdAt };
    case 'person.deceased':
      return { kind: 'deceased', label: L.deceased, detail: null, date: str('deceasedDate') ?? entry.createdAt };
    case 'person.transferred':
      return { kind: 'transferred', label: L.transferred, detail: null, date: entry.createdAt };
    case 'attribute.changed': {
      const changed = Array.isArray(meta.changed) ? (meta.changed as unknown[]).map(String) : [];
      return { kind: 'edited', label: L.edited, detail: changed.length ? changed.join(', ') : null, date: entry.createdAt };
    }
    default:
      return { kind: 'added', label: entry.action, detail: null, date: entry.createdAt };
  }
}

const CURRENT_STATUS_PILL: Record<string, string> = {
  ACTIVE: 'pill-success',
  MOVED_OUT: 'pill-pending',
  DECEASED: 'pill-neutral',
};

// "Status history" — a refined vertical timeline of the resident's lifecycle,
// newest first, built from the change log. Header carries the current status.
type HistoryFilter = 'all' | 'status' | 'edits' | 'transfers';

const HISTORY_GROUP: Record<HistoryKind, Exclude<HistoryFilter, 'all'>> = {
  added: 'status', moved: 'status', returned: 'status', deceased: 'status',
  transferred: 'transfers', edited: 'edits',
};

const HISTORY_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: 'all', label: dv.register.historyFilter.all },
  { key: 'status', label: dv.register.historyFilter.status },
  { key: 'edits', label: dv.register.historyFilter.edits },
  { key: 'transfers', label: dv.register.historyFilter.transfers },
];

function StatusHistory({ person }: { person: Person }): JSX.Element {
  const { data: history } = useQuery({
    queryKey: ['person-history', person.id],
    queryFn: () => registerApi.personHistory(person.id),
  });
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [page, setPage] = useState(0);

  const entries = (history ?? []).map((raw) => ({ raw, ...describeHistory(raw) }));
  const q = query.trim().toLowerCase();
  const filtered = entries.filter((e) => {
    if (filter !== 'all' && HISTORY_GROUP[e.kind] !== filter) return false;
    if (q && !`${e.label} ${e.detail ?? ''} ${e.raw.actor ?? ''}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const isPristine = filter === 'all' && q === '';

  const PER_PAGE = 20;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pageCount - 1);
  const pageItems = filtered.slice(current * PER_PAGE, current * PER_PAGE + PER_PAGE);

  if (entries.length === 0) {
    return (
      <section className="card-paper p-4">
        <p className="mono-meta text-muted-foreground">{dv.register.historyEmpty}</p>
      </section>
    );
  }

  return (
    <section className="card-paper space-y-3 p-4">
      {/* search + type filter */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Icon name="search" size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input ps-9"
            placeholder={dv.register.historySearch}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            aria-label={dv.register.historySearch}
          />
        </div>
        <div className="flex shrink-0 gap-0.5 rounded-lg border border-border p-0.5">
          {HISTORY_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => { setFilter(f.key); setPage(0); }}
              aria-pressed={filter === f.key}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="mono-meta text-muted-foreground py-6 text-center">{dv.register.historyNoMatch}</p>
      ) : (
        <ol className="relative px-0.5 pt-1">
          {pageItems.map((e, i) => {
            const { icon, node } = HISTORY_STYLE[e.kind];
            const isLast = i === pageItems.length - 1;
            const isLatest = i === 0 && current === 0 && isPristine; // "current" only on the first, unfiltered page
            return (
              <li key={`${e.raw.action}-${e.raw.createdAt}-${i}`} className="relative flex gap-3.5 pb-6 last:pb-1">
                {!isLast && <span aria-hidden className="absolute start-[17px] top-10 bottom-0 w-px bg-border" />}
                <span
                  aria-hidden
                  className={`relative z-10 mt-0.5 grid size-9 shrink-0 place-items-center rounded-full ring-1 ${node} ${isLatest ? 'ring-2' : ''}`}
                >
                  <Icon name={icon} size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-semibold leading-snug text-ink">{e.label}</p>
                    <time className="mono-meta shrink-0 text-muted-foreground" title={fmtDate(e.date)}>{relativeTime(e.raw.createdAt)}</time>
                  </div>
                  {e.detail && <p className="mt-0.5 text-sm text-muted-foreground leading-snug">{e.detail}</p>}
                  <div className="mt-2 flex items-center gap-2">
                    {e.raw.actor && (
                      <span className={`grid size-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${avatarTint(e.raw.actor)}`} aria-hidden>
                        {initials(e.raw.actor)}
                      </span>
                    )}
                    <span className="mono-meta text-muted-foreground truncate">
                      {e.raw.actor ? `${e.raw.actor} · ` : ''}{fmtDate(e.date)}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {filtered.length > PER_PAGE && (
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <button
            type="button"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
            className="btn-secondary btn-sm disabled:opacity-40"
          >
            {dv.common.prev}
          </button>
          <span className="mono-meta text-muted-foreground">
            {current * PER_PAGE + 1}–{Math.min((current + 1) * PER_PAGE, filtered.length)} {dv.common.of} {filtered.length}
          </span>
          <button
            type="button"
            disabled={current >= pageCount - 1}
            onClick={() => setPage(current + 1)}
            className="btn-secondary btn-sm disabled:opacity-40"
          >
            {dv.common.next}
          </button>
        </div>
      )}
    </section>
  );
}

export function PersonDetail(): JSX.Element {
  const { personId } = useParams({ from: '/people/$personId' });
  const { data: person, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['person', personId],
    queryFn: () => registerApi.person(personId),
  });

  if (isLoading) {
    return <div className="page"><p className="status-label text-muted-foreground" role="status">{dv.common.loading}</p></div>;
  }
  if (isError || !person) {
    const notFound = (queryError as AxiosLikeError)?.response?.status === 404;
    return (
      <div className="page space-y-4">
        <Link to="/houses" className="btn-ghost"><Icon name="arrow_forward" size={15} /> {dv.houses.title}</Link>
        <p className="card-paper p-6 text-center text-sm text-muted-foreground">
          {notFound ? dv.register.personNotFound : dv.common.error}
        </p>
      </div>
    );
  }

  return <PersonPage person={person} />;
}

// The resident page is tabbed: "Details" (the editable form + status + move) and
// "History" (the full activity timeline).
function PersonPage({ person }: { person: Person }): JSX.Element {
  const [tab, setTab] = useState<'details' | 'history'>('details');
  const TABS = [
    { key: 'details' as const, label: dv.register.tabDetails },
    { key: 'history' as const, label: dv.register.tabHistory },
  ];
  return (
    <div className="page space-y-5">
      <Link to="/houses/$houseId" params={{ houseId: person.house.id }} className="btn-ghost">
        <Icon name="arrow_forward" size={15} /> {person.house.name}
      </Link>

      <header className="flex items-start justify-between gap-3">
        <h1 className="font-serif text-3xl tracking-tight">{person.fullName}</h1>
        <div className="mt-1 flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Link to="/family" search={{ id: person.id }} className="btn-ghost px-3 py-1.5">
            <Icon name="family_history" size={16} /> Family tree
          </Link>
          <OwnerToggle person={person} />
          <span className={`pill ${CURRENT_STATUS_PILL[person.status] ?? 'pill-neutral'}`}>
            {dv.register.statusLabel[person.status]}
          </span>
        </div>
      </header>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            aria-current={tab === t.key ? 'page' : undefined}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-primary text-ink'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'details' ? <PersonEditor person={person} /> : <StatusHistory person={person} />}
    </div>
  );
}
